import {
  getTaxDecimal,
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  updateQuoteDetail,
  useDeleteQuoteDetails,
  useIsServiceQuoteOrChangeOrder,
  useQuoteDetailsQuery,
  useQuoteDetailsSummary,
  useQuoteQuery,
} from '@/lib/api'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import { LABOR_SUMMARY_NAME_TO_KEY_MAP, LABOR_SUMMARY_PRODUCT_ID_MAP, LABOR_SUMMARY_PRODUCT_NAMES } from '@/lib/utils'
import { Decimal } from 'decimal.js'
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react'

export const LaborPlugin = memo(function LaborPlugin() {
  const { data: quote } = useQuoteQuery()

  const isDraft = isQuoteDraftEditable(quote)
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)
  const isService = useIsServiceQuoteOrChangeOrder()

  const laborSummaryProductNames = useMemo(
    () =>
      LABOR_SUMMARY_PRODUCT_NAMES.filter((productName) =>
        !isService ? productName !== 'LABOR SUMMARY:SERVICE' : true
      ),
    [isService]
  )

  if (!isDraft || isDtoolsSiLocked) return null

  return (
    <Fragment>
      {laborSummaryProductNames.map((productName) => (
        <LaborItem key={'labor-summary-plugin-item-' + productName} productName={productName} />
      ))}
    </Fragment>
  )
})

const LaborItem = memo(function LaborItem({
  productName,
}: {
  productName: keyof typeof LABOR_SUMMARY_NAME_TO_KEY_MAP
}) {
  const { data: quote } = useQuoteQuery()
  const { data: quoteDetails, dataUpdatedAt: quoteDetailsUpdatedAt } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
  })
  const { data: summary, dataUpdatedAt: summaryUpdatedAt } = useQuoteDetailsSummary({ laborKey: productName })
  const { mutate: deleteQuoteDetails, isPending: isDeletingQuoteDetails } = useDeleteQuoteDetails()

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)
  const sourceQueriesLoaded = isQueryLoaded(quoteDetailsUpdatedAt) && isQueryLoaded(summaryUpdatedAt)

  const laborQuoteDetail = useMemo(
    () => quoteDetails?.find((detail) => detail.productid.productid === LABOR_SUMMARY_PRODUCT_ID_MAP[productName]),
    [quoteDetails, productName]
  )

  const computeChange = useCallback(() => {
    if (!quote || !quoteDetails || !laborQuoteDetail) return null
    const unitPriceDecimal = new Decimal(laborQuoteDetail.priceperunit ?? 0)
    const newQuantityDecimal = new Decimal(summary.labor.hours).toDecimalPlaces(5)
    const newBase = newQuantityDecimal.mul(unitPriceDecimal).toDecimalPlaces(2)
    const newCost = new Decimal(summary.labor.cost).toDecimalPlaces(2)
    const newTax = laborQuoteDetail.msdyn_taxable ? getTaxDecimal(unitPriceDecimal, newQuantityDecimal) : new Decimal(0)
    if (
      newQuantityDecimal.equals(laborQuoteDetail.quantity) &&
      newTax.equals(laborQuoteDetail.tax ?? 0) &&
      newCost.equals(laborQuoteDetail.msdyn_costamount ?? 0)
    )
      return null
    const totalPriceWithTaxDecimal = newBase.plus(newTax).toDecimalPlaces(2)
    return {
      quantity: newQuantityDecimal.toNumber(),
      msdyn_costamount: newCost.toNumber(),
      baseamount: newBase.toNumber(),
      tax: newTax.toNumber(),
      extendedamount: totalPriceWithTaxDecimal.toNumber(),
    }
  }, [quote, quoteDetails, laborQuoteDetail, summary])

  const {
    isPending: isUpdatingLaborQuoteDetail,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: `labor-${productName}`,
    fingerprint: () => [quoteDetailsUpdatedAt, summaryUpdatedAt],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'labor', 'update-labor-quote-detail', productName],
    isReady: () => sourceQueriesLoaded,
    notReadyMessage: 'Labor summary is still loading. Please try activating again in a moment.',
    isDirty: () => computeChange() !== null,
    mutationFn: async () => {
      if (!quote || !quoteDetails || !laborQuoteDetail) {
        throw new Error('Missing quote or labor quote detail')
      }

      const duplicateLaborQuoteDetails = quoteDetails.filter(
        (detail) =>
          detail.productid.productid === LABOR_SUMMARY_PRODUCT_ID_MAP[productName] &&
          detail.quotedetailid !== laborQuoteDetail.quotedetailid
      )

      if (duplicateLaborQuoteDetails.length && !isDeletingQuoteDetails) {
        deleteQuoteDetails(duplicateLaborQuoteDetails)
      }

      const change = computeChange()
      if (!change) return

      return updateQuoteDetail({
        quoteDetail: laborQuoteDetail,
        cpqFieldsBody: {},
        quoteDetailBody: change,
      })
    },
    onSuccess: async () => {
      addQueryKeys([QUERY_KEYS.quoteDetails(), QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' })])
    },
    onError: (error) => {
      console.error('LaborItem plugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (isUpdatingLaborQuoteDetail || !quote || !quoteDetails || !laborQuoteDetail || !sourceQueriesLoaded) return
    schedule(1000)
    return cancel
  }, [summary, sourceQueriesLoaded, isUpdatingLaborQuoteDetail, quote, quoteDetails, laborQuoteDetail, schedule, cancel])

  return null
})
